import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Button } from '@reservamos/elements';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import BillingModal from './BillingModal';

/**
 * BillingSection component.
 *
 * @param {object} props - purchaseToken, validateChangeDocument, changeDocument, operationNumbers, email, getSelectedTicket, isTicketSelected, onRefreshData.
 * @param {string} props.purchaseToken - Token used for ticket purchase.
 * @param {Function} props.validateChangeDocument - Function to validate the change document.
 * @param {Function} props.changeDocument - Function to change the document.
 * @param {Array<string>} props.operationNumbers - Operation numbers for the ticket.
 * @param {string} props.email - Email address.
 * @param {Function} props.getSelectedTicket - Function to get the selected ticket.
 * @param {boolean} props.isTicketSelected - Whether a ticket is currently selected.
 * @param {Function} props.onRefreshData - Function to refresh ticket data after successful operation.
 *
 * @returns {JSX.Element|null} The rendered billing section or null if feature is disabled.
 */
const BillingSection = ({
  purchaseToken,
  validateChangeDocument,
  changeDocument,
  operationNumbers,
  email,
  getSelectedTicket,
  isTicketSelected,
  onRefreshData,
}) => {
  const { t } = useTranslation('exchange');
  const features = useWhitelabelFeatures();
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingModalType, setBillingModalType] = useState(null);

  const selectedTicket = useMemo(() => {
    return getSelectedTicket();
  }, [getSelectedTicket]);

  /**
   * makeChangeDocument Function
   *
   * @param {Object} payloadValidation - The validation payload that was used
   * @returns {Promise<Object>} A promise that resolves to the change document response
   */
  const makeChangeDocument = async (payloadValidation) => {
    try {
      const changeDocumentPayload = {
        ...payloadValidation,
        reserveCode: Array.isArray(operationNumbers)
          ? operationNumbers.join(',')
          : operationNumbers,
        email,
      };

      const changeResponse = await changeDocument(changeDocumentPayload);

      return changeResponse;
    } catch (error) {
      return {
        success: false,
        message: t('modal_confirm_billing.change_document_failed'),
      };
    }
  };

  /**
   * handleBillingModalOpen Function
   *
   * Handles the opening of the billing modal with the specified type.
   *
   * @param {string} modalType - The type of billing modal to open ('invoice' or 'correction')
   * @returns {void}
   */
  const handleBillingModalOpen = useCallback((modalType) => {
    setBillingModalType(modalType);
    setShowBillingModal(true);
  }, []);

  /**
   * handleBillingModalClose Function
   *
   * Handles the closing of the billing modal and refreshes the ticket data.
   *
   * This function is called when the billing modal is closed, and it performs
   * the necessary actions to refresh the ticket data to reflect any changes.
   *
   * @param {boolean} shouldRefresh - Whether to refresh the ticket data after closing
   * @returns {void}
   */
  const handleBillingModalClose = useCallback(
    (shouldRefresh) => {
      setShowBillingModal(false);
      setBillingModalType(null);

      // Only refresh if shouldRefresh is explicitly true
      if (shouldRefresh === true && onRefreshData) {
        onRefreshData();
      }
    },
    [onRefreshData],
  );

  // Don't render if the feature is disabled
  if (!features.SHOW_BILLING_BUTTON) {
    return null;
  }

  return (
    <>
      <Button
        buttonType="submit"
        text={t('ticket_list.button_billing')}
        variant="accent"
        isRounded
        withHeight
        fullWidthOnSmall
        className="inline-flex w-auto"
        onClick={() => handleBillingModalOpen('invoice')}
        isDisabled={!isTicketSelected}
      />
      <Button
        buttonType="submit"
        text={t('ticket_list.button_billing_change')}
        variant="accent"
        isRounded
        withHeight
        fullWidthOnSmall
        className="inline-flex w-auto"
        onClick={() => handleBillingModalOpen('correction')}
        isDisabled={!isTicketSelected}
      />
      <BillingModal
        showModal={showBillingModal}
        onClose={handleBillingModalClose}
        purchaseToken={purchaseToken}
        validateChangeDocument={validateChangeDocument}
        makeChangeDocument={makeChangeDocument}
        selectedTicket={selectedTicket}
        modalType={billingModalType}
      />
    </>
  );
};

BillingSection.propTypes = {
  purchaseToken: PropTypes.string.isRequired,
  validateChangeDocument: PropTypes.func.isRequired,
  changeDocument: PropTypes.func.isRequired,
  operationNumbers: PropTypes.arrayOf(PropTypes.string).isRequired,
  email: PropTypes.string.isRequired,
  getSelectedTicket: PropTypes.func.isRequired,
  isTicketSelected: PropTypes.bool.isRequired,
  onRefreshData: PropTypes.func,
};

export default BillingSection;
